/** PR-6 — types + fetchers for the prompt-ops admin dashboard.
 *
 *  Surfaces the PR-1a prompt registry (``agent_prompt_versions`` +
 *  ``reasoning_traces`` provenance) and the PR-1b eval signal
 *  (``eval_examples``) and wires the two human-gated writes: the officer
 *  "which step failed" attribution flag and admin promote/rollback. All
 *  against ``/api/admin/prompt-ops`` (admin-gated, except the attribution
 *  write + stages list which any authenticated user may call). */

import { apiFetch } from './api'

const BASE = '/api/admin/prompt-ops'

// ── Registry ────────────────────────────────────────────────────────────────
export interface PromptVersionSummary {
  id: string
  agent_name: string
  version: number
  prompt_hash: string
  is_active: boolean
  created_at: string | null
  activated_by_user_id: string | null
  activated_at: string | null
  prompt_chars: number
}

export interface DiffLine {
  kind: 'add' | 'del' | 'ctx' | 'meta'
  text: string
}

export interface PromptDiff {
  from_version: number | null
  to_version: number
  added: number
  removed: number
  lines: DiffLine[]
}

export interface RegistryStageEntry {
  agent_name: string
  active: PromptVersionSummary | null
  prior: PromptVersionSummary | null
  total_versions: number
  diff: PromptDiff | null
}

export interface RegistryOverview {
  agents: RegistryStageEntry[]
  generated_at: string
}

export interface PromptVersionDetail extends PromptVersionSummary {
  prompt_text: string
}

export interface AgentVersions {
  agent_name: string
  versions: PromptVersionDetail[]
  diffs: PromptDiff[]
}

// ── Eval volume ──────────────────────────────────────────────────────────────
export interface EvalStageRollup {
  stage: string
  positive: number
  negative: number
  clean: number
  ambiguous: number
  total: number
}

export interface EvalStatCell {
  stage: string
  verdict: string
  attribution_confidence: string
  split: string
  count: number
}

export interface EvalStats {
  cells: EvalStatCell[]
  by_stage: EvalStageRollup[]
  total_examples: number
  ambiguous_unresolved: number
  generated_at: string
}

// ── Eval examples + attribution ──────────────────────────────────────────────
export interface EvalExample {
  id: string
  stage: string
  call_type: string | null
  verdict: string
  attribution_confidence: string
  split: string
  object_ref: string | null
  failure_mode: string | null
  gold_output: Record<string, unknown> | null
  proposal_id: string | null
  document_id: string | null
  trace_id: string | null
  replayable: boolean
  attributed_by_user_id: string | null
  attributed_at: string | null
  created_at: string | null
}

export interface EvalExampleList {
  examples: EvalExample[]
  count: number
}

// ── MIPRO ────────────────────────────────────────────────────────────────────
export interface MiproRun {
  id: string
  agent_name: string
  status: string
  baseline_score: number | null
  compiled_score: number | null
  compiled_prompt_version_id: string | null
  started_at: string | null
  completed_at: string | null
}

export interface MiproRuns {
  runs: MiproRun[]
  pending_integration: boolean
  note: string
}

export interface PromoteResult {
  agent_name: string
  active_version: number
  previous_active_version: number | null
  activated_by_user_id: string
  activated_at: string
}

// ── Serving map ─────────────────────────────────────────────────────────────
export interface ServingMapRow {
  agent_name: string
  prompt_ids: string[]
  serving: 'code' | 'promoted'
  active_version: number | null
  calls_24h: number
  calls_7d: number
  tokens_7d: number
  last_call_at: string | null
}

export interface ServingMap {
  rows: ServingMapRow[]
  eval_examples_by_stage: Record<string, number>
  generated_at: string
}

// ── Fetchers ─────────────────────────────────────────────────────────────────
export function fetchServingMap(): Promise<ServingMap> {
  return apiFetch<ServingMap>(`${BASE}/serving-map`)
}

export function fetchRegistry(): Promise<RegistryOverview> {
  return apiFetch<RegistryOverview>(`${BASE}/registry`)
}

export function fetchAgentVersions(agentName: string): Promise<AgentVersions> {
  return apiFetch<AgentVersions>(`${BASE}/registry/${encodeURIComponent(agentName)}`)
}

export function fetchEvalStats(): Promise<EvalStats> {
  return apiFetch<EvalStats>(`${BASE}/eval-stats`)
}

export function fetchEvalExamples(
  params: {
    attribution_confidence?: string
    stage?: string
    verdict?: string
    limit?: number
  } = {}
): Promise<EvalExampleList> {
  const qs = new URLSearchParams()
  if (params.attribution_confidence) qs.set('attribution_confidence', params.attribution_confidence)
  if (params.stage) qs.set('stage', params.stage)
  if (params.verdict) qs.set('verdict', params.verdict)
  if (params.limit) qs.set('limit', String(params.limit))
  const q = qs.toString()
  return apiFetch<EvalExampleList>(`${BASE}/eval-examples${q ? `?${q}` : ''}`)
}

export function fetchStages(): Promise<string[]> {
  return apiFetch<string[]>(`${BASE}/stages`)
}

export function attributeExample(
  exampleId: string,
  stage: string,
  note?: string
): Promise<EvalExample> {
  return apiFetch<EvalExample>(`${BASE}/eval-examples/${exampleId}/attribute`, {
    method: 'POST',
    body: JSON.stringify({ stage, note: note ?? null }),
  })
}

export function promoteVersion(agentName: string, version: number): Promise<PromoteResult> {
  return apiFetch<PromoteResult>(`${BASE}/registry/${encodeURIComponent(agentName)}/promote`, {
    method: 'POST',
    body: JSON.stringify({ version }),
  })
}

export function rollbackVersion(agentName: string): Promise<PromoteResult> {
  return apiFetch<PromoteResult>(`${BASE}/registry/${encodeURIComponent(agentName)}/rollback`, {
    method: 'POST',
  })
}

export function fetchMiproRuns(): Promise<MiproRuns> {
  return apiFetch<MiproRuns>(`${BASE}/mipro/runs`)
}
